import React from 'react';

// The in-flight indicator on a button: a conventional ring spinner that sits
// inline, to the RIGHT of the label.
//
// ── WHY THIS REPLACED THE TRAVELLING RIM ────────────────────────────────────
//
// ButtonSpinnerRing painted a masked arc around the button's border. On paper
// it was the more tasteful thing; in practice it did not read, and on some
// setups it did not appear to run at all. Two reasons, and the second is the
// one that mattered:
//
//   1. A two-to-three pixel arc on a wide, short button spends most of its
//      cycle on the horizontal edges, where a few degrees of rotation covers
//      almost no pixels. The motion was there and looked like a flicker.
//   2. It was gated behind `motion-safe:`. Under prefers-reduced-motion — a
//      system setting, not a browser one, and on by default for anyone who has
//      ever turned on Reduce Motion — the arc froze into a static gradient rim.
//      Indistinguishable from a slightly brighter border. Nothing moving at
//      all, which is exactly the report.
//
// So this one is NOT motion-safe-gated. A loading spinner is not decoration;
// it is the state itself, and WCAG's reduced-motion guidance is about
// vestibular triggers — large parallax, spinning backgrounds — not a 14px
// indicator. It slows down under the preference rather than stopping, which
// keeps the meaning and drops the insistence.
//
// Rendered with an SVG rather than a bordered div because a 3/4 arc drawn with
// `border-top-color: transparent` inherits the button's own text colour
// unevenly across variants; `currentColor` on a stroked circle takes the
// label's colour exactly, so it is right on Aurum-on-dark and ink-on-metallic
// without either knowing about the other.

const SIZE = 15;
const STROKE = 2;
const R = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * R;

export default function ButtonSpinner({ className = '' }) {
  return (
    <svg
      aria-hidden="true"
      data-testid="button-spinner"
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      // `animate-spin` unconditionally, with a longer duration under the
      // reduced-motion preference — see the note above.
      className={`shrink-0 animate-spin motion-reduce:[animation-duration:2.4s] ${className}`.trim()}
      style={{ animationDuration: '0.7s' }}
    >
      {/* The track, so the gap has something to travel against. Without it the
          arc reads as a comma rather than a spinner. */}
      <circle
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={R}
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        opacity="0.25"
      />
      {/* Three-quarters of the circumference, so a quarter stays open. */}
      <circle
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={R}
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeDasharray={`${CIRCUMFERENCE * 0.75} ${CIRCUMFERENCE}`}
      />
    </svg>
  );
}
